import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Sparkle, ChartBar, Cow } from "@phosphor-icons/react";
import { API, formatNum, formatDate } from "@/lib/gado";

function ConfidenceBadge({ value }) {
  const pct = Math.round((value || 0) * 100);
  const color =
    pct >= 80
      ? "bg-emerald-100 text-emerald-800 border-emerald-300"
      : pct >= 60
      ? "bg-amber-100 text-amber-800 border-amber-300"
      : "bg-rose-100 text-rose-800 border-rose-300";
  return (
    <span className={`inline-flex items-center border rounded-full px-3 py-1 text-xs font-semibold ${color}`}>
      Confiança {pct}%
    </span>
  );
}

function DistributionBars({ perAnimal }) {
  if (!perAnimal || perAnimal.length === 0) return null;
  const weights = perAnimal.map((a) => Number(a.weight_kg) || 0);
  const minW = Math.min(...weights);
  const maxW = Math.max(...weights);
  const bins = 6;
  const step = (maxW - minW) / bins || 1;
  const counts = Array(bins).fill(0);
  weights.forEach((w) => {
    const b = Math.min(bins - 1, Math.max(0, Math.floor((w - minW) / step)));
    counts[b] += 1;
  });
  const maxCount = Math.max(...counts) || 1;
  return (
    <div>
      <div className="flex items-end gap-1 h-24">
        {counts.map((c, i) => (
          <div
            key={i}
            className="flex-1 rounded-t-sm bg-gado-green/70"
            style={{ height: `${(c / maxCount) * 100}%` }}
          />
        ))}
      </div>
      <div className="flex justify-between text-[10px] text-gado-muted mt-1 font-mono">
        <span>{formatNum(minW, 0)} kg</span>
        <span>{formatNum(maxW, 0)} kg</span>
      </div>
    </div>
  );
}

export default function ShareAnalysis() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${API}/public/analysis/${token}`);
        if (!res.ok) throw new Error(res.status === 404 ? "Análise não encontrada" : "Falha ao carregar");
        setData(await res.json());
      } catch (e) {
        setError(e.message || "Falha ao carregar");
      } finally {
        setLoading(false);
      }
    })();
  }, [token]);

  if (loading) {
    return (
      <div className="min-h-screen grid place-items-center bg-gado-bg text-gado-muted">
        Carregando análise…
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen grid place-items-center bg-gado-bg p-6">
        <div className="text-center max-w-sm">
          <Cow size={48} className="mx-auto text-gado-line mb-3" />
          <h1 className="font-cabinet font-bold text-gado-ink text-xl mb-1">Análise indisponível</h1>
          <p className="text-sm text-gado-muted">{error}</p>
        </div>
      </div>
    );
  }

  const r = data?.result || {};
  const owner = data?.owner || {};

  return (
    <div className="min-h-screen bg-gado-bg text-gado-ink" data-testid="share-analysis-page">
      {/* Branded top bar */}
      <div className="bg-gado-greenDark text-white">
        <div className="max-w-2xl mx-auto px-4 py-4 flex items-center gap-3">
          <img src="/logo-128.png" alt="GadoPro" className="h-9 w-9 rounded-md object-contain" />
          <div className="min-w-0">
            <p className="font-cabinet font-bold leading-tight">
              Gado<span className="text-gado-goldLight">Pro</span>
            </p>
            <p className="text-[10px] text-white/70 tracking-widest uppercase">Análise por IA · Beta</p>
          </div>
        </div>
      </div>

      <div className="max-w-2xl mx-auto p-4 space-y-4">
        {/* Video keyframe thumbnail — helps the recipient recognize the batch instantly */}
        <img
          src={`${API}/public/analysis/${token}/thumbnail`}
          alt="Frame do lote analisado"
          data-testid="share-thumbnail"
          className="w-full max-h-72 object-cover rounded-xl border border-gado-line bg-black"
          onError={(e) => { e.target.style.display = "none"; }}
        />

        {/* Broker card */}
        {(owner.fantasy_name || owner.name) && (
          <div className="rounded-xl border border-gado-line bg-white p-4 flex items-center gap-3">
            {owner.logo ? (
              <img
                src={owner.logo.startsWith("data:") ? owner.logo : `data:image/*;base64,${owner.logo}`}
                alt="Logo"
                className="h-12 w-12 rounded-md object-contain bg-gado-bg"
              />
            ) : (
              <div className="h-12 w-12 rounded-md bg-gado-green/10 grid place-items-center">
                <Cow size={22} className="text-gado-green" weight="fill" />
              </div>
            )}
            <div className="min-w-0">
              <p className="text-[10px] uppercase tracking-widest text-gado-muted">Enviado por</p>
              <p className="font-cabinet font-bold text-gado-ink truncate">
                {owner.fantasy_name || owner.name}
              </p>
              {owner.phone && <p className="text-xs text-gado-muted">{owner.phone}</p>}
            </div>
          </div>
        )}

        {/* Result card */}
        <div className="rounded-xl border border-gado-line bg-white p-5 space-y-4">
          <div className="flex items-start justify-between gap-3">
            <div>
              <div className="flex items-center gap-2">
                <Sparkle size={16} weight="fill" className="text-gado-gold" />
                <p className="text-[10px] uppercase tracking-widest text-gado-muted">
                  Análise por IA · Gemini 3.1 Pro
                </p>
              </div>
              <h1 className="font-cabinet text-2xl font-extrabold text-gado-ink mt-1">
                Lote de {r.total_animals || 0} cabeça{r.total_animals === 1 ? "" : "s"}
              </h1>
              <p className="text-xs text-gado-muted">
                {r.breed_guess || "—"} · {r.category_guess || "—"} · {formatDate(data?.created_at)}
              </p>
            </div>
            <ConfidenceBadge value={r.confidence} />
          </div>

          <div className="grid grid-cols-3 gap-2 text-center">
            <div className="rounded-lg bg-gado-bg/60 py-3 px-1">
              <p className="text-[10px] uppercase tracking-wider text-gado-muted">Cabeças</p>
              <p className="font-mono font-bold text-gado-ink text-lg">{r.total_animals || 0}</p>
            </div>
            <div className="rounded-lg bg-gado-bg/60 py-3 px-1">
              <p className="text-[10px] uppercase tracking-wider text-gado-muted">Peso médio</p>
              <p className="font-mono font-bold text-gado-ink text-lg">{formatNum(r.avg_weight_kg, 0)} kg</p>
            </div>
            <div className="rounded-lg bg-gado-green/5 py-3 px-1">
              <p className="text-[10px] uppercase tracking-wider text-gado-muted">Peso total</p>
              <p className="font-mono font-bold text-gado-green text-lg">{formatNum(r.total_weight_kg, 0)} kg</p>
            </div>
          </div>

          {r.notes && (
            <p className="text-xs italic text-gado-muted border-l-2 border-gado-gold pl-2">{r.notes}</p>
          )}

          {r.per_animal && r.per_animal.length > 0 && (
            <div>
              <div className="flex items-center justify-between mb-1">
                <p className="text-[10px] uppercase tracking-wider text-gado-muted">Distribuição de pesos</p>
                <ChartBar size={12} className="text-gado-muted" />
              </div>
              <DistributionBars perAnimal={r.per_animal} />
            </div>
          )}
        </div>

        {/* Legal */}
        <p className="text-xs italic text-gado-muted text-center px-4">
          Estimativa gerada por IA a partir de vídeo. Os valores são referências para pré-avaliação
          e não substituem pesagem oficial em balança.
        </p>
        <div className="text-center pt-2">
          <a
            href="/"
            className="inline-flex items-center gap-1.5 text-xs font-semibold text-gado-green hover:underline"
          >
            Conheça o GadoPro
          </a>
        </div>
      </div>
    </div>
  );
}
